import axios from 'axios';
import retry from 'async-retry';

const progressHandler =
  ({ progressCache, handleUpload, PartNumber, fileInfo, block }) =>
  (e) => {
    const { fileType, fileName, fileSize, filePreview } = fileInfo;
    const blobProgress = Math.round((e.loaded * 100) / e.total);
    // eslint-disable-next-line no-param-reassign
    progressCache[PartNumber] = blobProgress * (e.total / fileSize);
    const progress = Object.keys(progressCache).reduce(
      (total, i) => total + progressCache[i],
      0,
    );
    handleUpload(
      {
        name: fileName,
        type: fileType,
        preview: filePreview,
        progress: Math.round(progress),
      },
      false,
      block,
    );
  };

/** Uploads a part to s3 */
const uploadToS3 = async ({
  file,
  fileInfo,
  signedUrl,
  block,
  handleUpload,
  progressCache,
  PartNumber,
}) =>
  axios.put(signedUrl, file, {
    headers: { 'Content-Type': fileInfo.fileType },
    onUploadProgress: progressHandler({
      progressCache,
      handleUpload,
      PartNumber,
      fileInfo,
      block,
    }),
  });

/** Marks start of a Multipart Upload */
const startUpload = async ({ fileName, fileType, uploadUrl, storage }) =>
  axios.post(
    `${uploadUrl}/start`,
    {
      fileName,
      fileType,
      storage,
    },
    {
      withCredentials: true,
    },
  );

/** Fetches signedUrls to upload from browser for list of part */
const getSignedRequests = ({ Key, UploadId, totalParts, uploadUrl, storage }) =>
  axios.post(
    `${uploadUrl}/url`,
    {
      Key,
      UploadId,
      totalParts,
      storage,
    },
    {
      withCredentials: true,
    },
  );
/** Splits a file into chunks of 10MB and uploads them */
const uploadChunks = async ({
  file,
  uploadUrl,
  Key,
  UploadId,
  block,
  handleUpload,
  storage,
  chunkSize = 10000000, // 10MB
  retries = 5,
}) => {
  const fileSize = file.size || 0;
  // getting number of chunks
  const numChunks = Math.floor(fileSize / chunkSize) + 1;
  const progressCache = {};
  const PartData = {};

  for (let index = 1; index < numChunks + 1; index += 1) {
    const start = (index - 1) * chunkSize;
    const end = index * chunkSize;
    // splitting a file into a chunk/part
    const blob = index < chunkSize ? file.slice(start, end) : file.slice(start);

    PartData[index] = { blob };
  }

  // fetching signedUrls for all parts
  /* eslint-disable-next-line no-await-in-loop */
  const {
    data: { signedUrls },
  } = await getSignedRequests({
    Key,
    UploadId,
    totalParts: numChunks,
    uploadUrl,
    storage,
  });

  const fileInfo = {
    fileType: file.type,
    fileName: file.name,
    fileSize: file.size,
    filePreview: file.preview,
  };

  // uploads a part in an async manner
  const promisesArray = signedUrls.map(({ PartNumber, signedUrl }) =>
    retry(
      () =>
        uploadToS3({
          file: PartData[PartNumber].blob,
          fileInfo,
          signedUrl,
          block,
          handleUpload,
          progressCache,
          PartNumber,
        }),
      { retries },
    ),
  );
  const resolvedArray = await Promise.all(promisesArray);
  // joining the uploaded parts data
  return resolvedArray.map((resp, i) => ({
    ETag: resp.headers.etag,
    PartNumber: i + 1,
  }));
};

/** Marks a multipart upload complete */
const completeUpload = ({ Parts, UploadId, Key, uploadUrl, storage }) =>
  axios.post(
    `${uploadUrl}/complete`,
    {
      Parts,
      Key,
      UploadId,
      storage,
    },
    {
      withCredentials: true,
    },
  );

/** Aborts a multipart upload */
const abortUpload = ({ UploadId, Key, uploadUrl, storage }) =>
  axios.post(
    `${uploadUrl}/abort`,
    {
      Key,
      UploadId,
      storage,
    },
    {
      withCredentials: true,
    },
  );

/** Splits file into chunks and uploads them
 * On failure it aborts the request to remove the uploaded parts
 */
const splitFileAndUpload = async ({
  file,
  uploadUrl,
  Key,
  UploadId,
  url,
  block,
  handleUpload,
  retries,
  storage,
}) => {
  try {
    const Parts = await uploadChunks({
      file,
      uploadUrl,
      Key,
      UploadId,
      block,
      handleUpload,
      retries,
      storage,
    });

    const {
      data: { url: fileUrl },
    } = await completeUpload({
      Parts,
      UploadId,
      Key,
      uploadUrl,
      storage,
    });

    // updates Parse with the new uploaded file
    const {
      data: { objectId },
    } = await axios.post(
      uploadUrl,
      {
        fileUrl: url,
        fileName: file.name,
      },
      {
        withCredentials: true,
      },
    );

    handleUpload(
      {
        name: file.name,
        type: file.type,
        fileUrl,
        fileItemId: objectId,
      },
      true,
      block,
    );

    return {
      name: file.name,
      type: file.type,
      fileUrl,
      fileItemId: objectId,
    };
  } catch (error) {
    await abortUpload({
      UploadId,
      Key,
      uploadUrl,
      storage,
    });
    handleUpload(
      {
        name: file.name,
        type: file.type,
        fileUrl: undefined,
        fileItemId: undefined,
      },
      false,
      block,
      error,
    );
    throw error;
  }
};

const noop = () => {};

/** Multipart File Uploader for th90 */
const uploadWithProgress = async ({
  uploadUrl,
  file,
  handleUpload,
  block,
  storage = 'parse',
  retries,
}) => {
  const {
    data: { UploadId, Key, url },
  } = await startUpload({
    fileName: file.name,
    fileType: file.type,
    uploadUrl,
    storage,
  });

  const uploadPromise = await splitFileAndUpload({
    file,
    uploadUrl,
    Key,
    UploadId,
    url,
    retries,
    block,
    handleUpload,
    storage,
  });

  return { UploadId, Key, promise: uploadPromise };
};

/** This is generic upload for a file */
const upload = async ({ uploadUrl, file, storage = 'parse' }) => {
  const {
    data: { UploadId, Key, url },
  } = await startUpload({
    fileName: file.name,
    fileType: file.type,
    uploadUrl,
    storage,
  });

  return splitFileAndUpload({
    file,
    uploadUrl,
    Key,
    UploadId,
    url,
    block: {},
    handleUpload: noop,
    storage,
  });
};

export { upload, uploadWithProgress, abortUpload, progressHandler };
